import React, { useEffect, useState } from 'react';
import axios from 'axios';
import config from '../../config/config';

const URGENCY_COLORS = {
  critical: 'danger',
  high: 'warning',
  medium: 'info',
  low: 'success',
};

function RemediationModal({ packageName, currentVersion, ecosystem, cves, isAbandoned, isDeprecated, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.post(`${config.BASE_URL}/ai/remediation`, {
      packageName,
      currentVersion,
      ecosystem,
      cves: cves || [],
      isAbandoned: !!isAbandoned,
      isDeprecated: !!isDeprecated,
    })
      .then(r => setResult(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [packageName, currentVersion, ecosystem, cves, isAbandoned, isDeprecated]);

  const handleCopy = () => {
    if (result?.fixCommand) {
      navigator.clipboard.writeText(result.fixCommand).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  return (
    <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="modal-dialog modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">💡 AI Remediation — <code>{packageName}</code></h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {loading && (
              <div className="d-flex align-items-center gap-3 py-4 text-secondary">
                <div className="spinner-border spinner-border-sm text-primary" />
                <span>Consulting AI…</span>
              </div>
            )}
            {error && <div className="alert alert-danger">{error}</div>}
            {result && !loading && (
              <div>
                {/* Urgency badge */}
                <div className="mb-3">
                  <span className={`badge bg-${URGENCY_COLORS[result.urgency] || 'secondary'} fs-6`}>
                    {result.urgency?.toUpperCase() || 'UNKNOWN'} urgency
                  </span>
                </div>

                {/* Summary */}
                <p className="mb-3">{result.summary}</p>

                {/* Recommended version */}
                {result.recommendedVersion && (
                  <div className="mb-2">
                    <strong>Recommended version:</strong>{' '}
                    <span className="text-success fw-semibold">{result.recommendedVersion}</span>
                  </div>
                )}

                {/* Fix command */}
                {result.fixCommand && (
                  <div className="mb-3">
                    <strong>Fix command:</strong>
                    <div className="position-relative mt-1">
                      <pre className="bg-dark text-light p-3 rounded small pe-5 mb-0">{result.fixCommand}</pre>
                      <button
                        className="btn btn-sm btn-outline-light position-absolute top-0 end-0 m-2"
                        onClick={handleCopy}
                        title="Copy to clipboard"
                      >
                        {copied ? '✅' : '📋'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Breaking changes */}
                {result.breakingChanges && (
                  <div className="mb-3">
                    <strong>Breaking changes:</strong>
                    <p className="text-secondary small mb-0 mt-1">{result.breakingChanges}</p>
                  </div>
                )}

                {/* Alternative package */}
                {result.alternativePackage && (
                  <div className="alert alert-warning py-2 small">
                    <strong>Alternative package:</strong> {result.alternativePackage}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RemediationModal;
